"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCollectionActivity, getCollectionHolders } from "@/features/collections/api";
import type { CollectionActivityItem, CollectionHolder, CollectionListingItem, CollectionOfferItem } from "@/features/collections/types";
import { activityKey, activityStopReason } from "@/features/collections/activity-pagination";
import { useCollectionOffers } from "@/features/collections/hooks/useCollectionOffers";
import { COLLECTION_LISTINGS_KEY, useCollectionListings } from "@/features/collections/hooks/useCollectionListings";
import { deriveOffersLastPage } from "@/features/collections/offers-pagination";
import { useBrowseParams } from "@/lib/browse/params";

/*
 * THE SINGLE COLLECTION'S TAB DATA — one hook per tab, shared by both page
 * designs so a design can never change what is fetched. Each is the legacy
 * tab's logic, moved and not changed: the same query keys, limits, paging
 * rules and retry (`resetQueries`, because `refetch()` was measured to be a
 * no-op on a failed query here).
 */

export const DETAIL_TABS = ["items", "activity", "holders", "analytics", "listings", "offers", "comments"] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];
/** Empty and hoisted: the items panel owns `?sort=`, so a tab change must not
 *  re-serialise a sort the user never touched. */
const NO_SORT_KEYS = [] as const;

/** The tab lives in `?tab=` — linkable, and Back leaves it. */
export function useDetailTab() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      tabs: DETAIL_TABS,
      defaultTab: "items",
      sortKeys: NO_SORT_KEYS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    tab,
    set
  } = useBrowseParams(t0);
  const t1 = (tab as DetailTab ?? "items") as DetailTab;
  let t2;
  if ($[1] !== set) {
    t2 = key => set({
      tab: key
    });
    $[1] = set;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      active: t1,
      choose: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/** Ten, by contract: the tab claims to be the top 10 and says so. */
export const HOLDERS_LIMIT = 10;

/** Top holders, with the share computed over the rows SHOWN (not a supply
 *  share), tolerating the three response shapes the endpoint has returned. */
export function useTopHolders(contract, lang) {
  const $ = _c(24);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== contract || $[1] !== lang) {
    t0 = ["collection-holders", contract, HOLDERS_LIMIT, lang];
    $[0] = contract;
    $[1] = lang;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  if ($[3] !== contract || $[4] !== lang) {
    t1 = () => getCollectionHolders(contract, HOLDERS_LIMIT, lang);
    $[3] = contract;
    $[4] = lang;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== queryKey || $[7] !== t1) {
    t2 = {
      queryKey,
      queryFn: t1,
      staleTime: 60000,
      gcTime: 300000
    };
    $[6] = queryKey;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data,
    isLoading,
    isSuccess
  } = useQuery(t2);
  let t3;
  if ($[9] !== data) {
    t3 = (data as any)?.data?.holders ?? (data as any)?.data?.list ?? [];
    $[9] = data;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const raw = t3;
  const totalOwned = raw.reduce(_temp, 0);
  let t4;
  if ($[11] !== raw || $[12] !== totalOwned) {
    let t5;
    if ($[14] !== totalOwned) {
      t5 = h_0 => {
        const owned = Number(h_0?.balance ?? h_0?.owned ?? 0);
        return {
          address: h_0?.wallet_address ?? h_0?.address ?? "",
          name: h_0?.wallet?.user?.name ?? h_0?.name ?? "",
          owned,
          percent: "",
          share: totalOwned > 0 ? owned / totalOwned * 100 : 0
        } as CollectionHolder & {
          share: number;
        };
      };
      $[14] = totalOwned;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = raw.map(t5);
    $[11] = raw;
    $[12] = totalOwned;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const holders = t4;
  let t5;
  if ($[16] !== queryClient || $[17] !== queryKey) {
    t5 = () => void queryClient.resetQueries({
      queryKey
    });
    $[16] = queryClient;
    $[17] = queryKey;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== holders || $[20] !== isLoading || $[21] !== isSuccess || $[22] !== t5) {
    t6 = {
      holders,
      isLoading,
      isSuccess,
      retry: t5
    };
    $[19] = holders;
    $[20] = isLoading;
    $[21] = isSuccess;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}

/** The activity log (transfers, offers, listings): paged by `activityStopReason` over the rows (the
 *  endpoint's `last_page` is untrustworthy), de-duplicated by `activityKey`. */
function _temp(sum, h) {
  return sum + Number(h?.balance ?? h?.owned ?? 0);
}
export function useActivityFeed(contract, lang) {
  const $ = _c(23);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== contract || $[1] !== lang) {
    t0 = ["collection-activity", contract, lang];
    $[0] = contract;
    $[1] = lang;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  if ($[3] !== contract || $[4] !== lang) {
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return getCollectionActivity(contract, lang, pageParam as number);
    };
    $[3] = contract;
    $[4] = lang;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== queryKey || $[7] !== t1) {
    t2 = {
      queryKey,
      queryFn: t1,
      initialPageParam: 1,
      getNextPageParam: _temp2,
      staleTime: 60000,
      gcTime: 300000
    };
    $[6] = queryKey;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const query = useInfiniteQuery(t2);
  let t3;
  if ($[9] !== query.data?.pages) {
    t3 = query.data?.pages ?? [];
    $[9] = query.data?.pages;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = activityStopReason(t3);
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const stop = t4;
  let t5;
  if ($[13] !== query.data?.pages) {
    const flat = (query.data?.pages ?? []).flatMap(_temp3);
    const seen = new Set();
    t5 = flat.filter(a => {
      const key = activityKey(a);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    $[13] = query.data?.pages;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const rows = t5;
  let t6;
  if ($[15] !== queryClient || $[16] !== queryKey) {
    t6 = () => void queryClient.resetQueries({
      queryKey
    });
    $[15] = queryClient;
    $[16] = queryKey;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== query || $[19] !== rows || $[20] !== stop || $[21] !== t6) {
    t7 = {
      query,
      rows,
      stop,
      retry: t6
    };
    $[18] = query;
    $[19] = rows;
    $[20] = stop;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
function _temp3(page) {
  return page?.data?.list ?? [];
}
function _temp2(_last, all) {
  return activityStopReason(all) === "more" ? all.length + 1 : undefined;
}
export const OFFERS_PAGE_SIZE = 10;

/** The read-only offers feed, one local page at a time (`?page=` belongs to
 *  Items), with the failed-page-change test measured for this app. */
export function useOffersPage(contract, lang) {
  const $ = _c(23);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  let t0;
  if ($[0] !== contract || $[1] !== lang || $[2] !== page) {
    t0 = ["collection-offers", contract, lang, OFFERS_PAGE_SIZE, page];
    $[0] = contract;
    $[1] = lang;
    $[2] = page;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== page) {
    t1 = {
      limit: OFFERS_PAGE_SIZE,
      page
    };
    $[4] = page;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const q = useCollectionOffers(contract, lang, t1);
  const meta = q.data?.data?.page;
  let t2;
  if ($[6] !== q.data?.data?.items) {
    t2 = q.data?.data?.items ?? [];
    $[6] = q.data?.data?.items;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = t2 as CollectionOfferItem[];
  const t4 = meta?.total ?? null;
  const t5 = meta?.current_page ?? page;
  let t6;
  if ($[8] !== meta) {
    t6 = meta ? deriveOffersLastPage(meta.total, meta.per_page) : 0;
    $[8] = meta;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = q.isPlaceholderData && !q.isFetching && q.failureCount > 0;
  let t8;
  if ($[10] !== queryClient || $[11] !== queryKey) {
    t8 = () => void queryClient.resetQueries({
      queryKey
    });
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== q.isFetching || $[14] !== q.isLoading || $[15] !== q.isSuccess || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = {
      offers: t3,
      total: t4,
      page: t5,
      lastPage: t6,
      setPage,
      isLoading: q.isLoading,
      isFetching: q.isFetching,
      isSuccess: q.isSuccess,
      backgroundFailure: t7,
      retry: t8
    };
    $[13] = q.isFetching;
    $[14] = q.isLoading;
    $[15] = q.isSuccess;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
export const LISTINGS_PAGE_SIZE = 10;

/** The open sale listings, one local page at a time, as the offers feed. */
export function useListingsPage(contract, lang) {
  const $ = _c(21);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  let t0;
  if ($[0] !== contract || $[1] !== lang || $[2] !== page) {
    t0 = [COLLECTION_LISTINGS_KEY, contract, lang, LISTINGS_PAGE_SIZE, page];
    $[0] = contract;
    $[1] = lang;
    $[2] = page;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== page) {
    t1 = {
      limit: LISTINGS_PAGE_SIZE,
      page
    };
    $[4] = page;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const q = useCollectionListings(contract, lang, t1);
  const meta = q.data?.data?.page;
  let t2;
  if ($[6] !== q.data?.data?.items) {
    t2 = q.data?.data?.items ?? [];
    $[6] = q.data?.data?.items;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = t2 as CollectionListingItem[];
  const t4 = meta?.total ?? null;
  const t5 = meta?.current_page ?? page;
  const t6 = meta?.last_page ?? 0;
  const t7 = q.isPlaceholderData && !q.isFetching && q.failureCount > 0;
  let t8;
  if ($[8] !== queryClient || $[9] !== queryKey) {
    t8 = () => void queryClient.resetQueries({
      queryKey
    });
    $[8] = queryClient;
    $[9] = queryKey;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== q.isFetching || $[12] !== q.isLoading || $[13] !== q.isSuccess || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = {
      listings: t3,
      total: t4,
      page: t5,
      lastPage: t6,
      setPage,
      isLoading: q.isLoading,
      isFetching: q.isFetching,
      isSuccess: q.isSuccess,
      backgroundFailure: t7,
      retry: t8
    };
    $[11] = q.isFetching;
    $[12] = q.isLoading;
    $[13] = q.isSuccess;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}